import {
  OnInit,
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  input,
  output,
  signal,
  computed,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import moment from 'moment';
import {
  ReplaySubject,
  combineLatest,
  distinctUntilChanged,
  interval,
  pairwise,
  startWith,
  takeUntil,
  throttle,
} from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { formatUserName } from 'app/core/utilities/user-util';
import Message from 'app/modules/social/message/message';
import MessageType from 'app/modules/social/message/message-type';
import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from 'app/modules/social/modal-simple-social/modal-simple-social.component';
import {
  PhotoBoothViewerComponent,
  PhotoViewerData,
} from 'app/modules/social/photo-booth-viewer/photo-booth-viewer.component';
import { FramedPicture, PhotoBoothPicture, PhotoBoothService } from 'app/modules/social/services/photo-booth.service';
import { MediaEmbedType, PresentationService } from 'app/modules/social/services/presentation/presentation.service';
import { SceneService } from 'app/modules/social/services/scene.service';
import { ShareScreenService } from 'app/modules/social/services/share-screen/share-screen.service';
import {
  GroupShareScreenParticipantService,
  GroupShareState,
} from 'app/modules/social/services/share-screen-group/group-share-screen-participant.service';
import { GroupShareScreenService } from 'app/modules/social/services/share-screen-group/group-share-screen.service';
import { SocialMessageService } from 'app/modules/social/services/social-message.service';
import { PLAYERS_IN_VIEW_INTERVAL } from 'app/modules/social/services/subscription/subscription.types';
import { LoadingTransitionLabel, TransitionService } from 'app/modules/social/services/transition.service';
import { Player } from 'app/modules/social/social-pixi-canvas/player';
import { InteractionModePresets } from 'app/modules/social/social-state-machine/social-state.constants';
import {
  ExtendedPresenter,
  socialAppActions,
  socialAppSelectors,
  SpecialAccessGroupModalMode,
  SpecialAccessGroupModalState,
} from 'app/modules/social/state/app';
import { SocialMediaShareService } from 'app/shared/services/social-media-share.service';
import { SocialMediaShareModalComponent } from 'app/shared/social-media-share-modal/social-media-share-modal.component';
import { AppState } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import {
  ANYONE_CAN_PRESENT,
  ANYONE_CAN_SHARE,
  ANYONE_CAN_SHARE_TO_GROUP,
  PHOTO_BOOTH,
  SAG_ENABLED,
} from 'entity/SocialSettings';
import User from 'entity/User';

import { PresentationEmbedModalComponent } from '../../presentation-embed-modal/presentation-embed-modal.component';

import { TransitionPresentationService } from './../../../services/transition-presentation.service';
import { PresentationMenuType } from './presentation-menu-dropdown.types';
import { delayFor } from 'app/core/utilities/async-utils';
import { WebsocketService } from 'app/modules/social/services/websocket.service';
import { ConvertGroupMessage, OutgoingMessageType } from 'messages/websocket.messages';
import { BrowserService } from 'app/modules/social/services/browser.service';

interface PhotoBoothNotification {
  message: string;
  duration: number;
}

@Component({
  selector: 'app-presentation-menu-dropdown',
  templateUrl: './presentation-menu-dropdown.component.html',
  styleUrls: ['./presentation-menu-dropdown.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresentationMenuDropdownComponent extends Loggable implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  private groupShareScreenService = inject(GroupShareScreenService);
  private messageService = inject(SocialMessageService);
  private photoBoothService = inject(PhotoBoothService);
  private presentationService = inject(PresentationService);
  private shareScreenService = inject(ShareScreenService);
  private sceneService = inject(SceneService);
  private socialMediaShareService = inject(SocialMediaShareService);
  private store = inject<Store<AppState>>(Store);
  private groupShareScreenParticipantService = inject(GroupShareScreenParticipantService);
  private transitionService = inject(TransitionService);
  private transitionPresentationService = inject(TransitionPresentationService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private websocketService = inject(WebsocketService);
  private browserService = inject(BrowserService);

  //Event to tell the parent if the menu is up, in which case it can do some z-index change
  //to prevent 'Capture Debug Data' button to stand on top of the opened menu.
  //If the menu is closed the button's z-index must be higher than the menu's.
  public readonly showDropdownEvent = output<boolean>();
  public readonly dropdownSelected = output<void>();
  public readonly type = input<PresentationMenuType>(PresentationMenuType.GROUP);
  public readonly user = input<User | undefined>(undefined);
  public readonly activeGroup = input<boolean>();

  public readonly isPresentationActive = signal(false);
  public readonly isGroupSharing = computed(() => this.groupShareScreenParticipantService.isGroupShareActiveSignal());
  public readonly allowPhotoBooth = signal(false);
  protected readonly vimeoEmbed = signal(false);
  protected MediaEmbedType = MediaEmbedType;

  private readonly _showDropdown = signal(false);
  protected readonly isHost = signal(false);
  private destroyed$ = new ReplaySubject<boolean>(1);
  private readonly social = signal<Social | undefined>(undefined);

  protected readonly isLoading = signal(false);
  protected readonly loadingText = signal('');
  protected groupShareState = GroupShareState;
  protected readonly groupShareButtonText = computed(() => {
    return this.groupShareScreenParticipantService.groupShareState();
  });

  private readonly group = signal<Player[]>([]);
  protected readonly isPlayerOutsideGroupLimit = signal(false);
  protected readonly isGroupShareEnabled = signal(false);
  protected readonly allowGroupShareScreen = signal(false);
  // protected readonly userCanGroupScreenShare = signal(false);
  protected readonly allowAnyonePresent = signal(false);
  protected readonly allowAnyoneShareScreen = signal(false);
  protected readonly enhancedScreenShare = signal(false);
  protected readonly hasClosedMenu = signal(false);
  protected readonly isConnectedRoom = signal(false);
  // TODO: set this to true when the feature is enabled
  protected readonly isSomeoneElsePresenting = signal(false);
  protected readonly isPresenting = signal(false);
  protected readonly activeMediaEmbed = signal(false);
  protected readonly canScreenShare = computed(() => {
    return (this.allowAnyoneShareScreen() || this.isHost() || this.isPresenting()) && !this.activeMediaEmbed();
  });

  protected readonly canPresentToAllRooms = computed(() => {
    return (
      this.crossRoomPresentationEnabled() && this.isConnectedRoom() && (this.allowAnyonePresent() || this.isHost())
    );
  });

  protected readonly isMobile = computed(() => this.browserService.isMobile());
  public readonly isMobileFakeDropdown = signal(false); // used to show the dropdown on mobile devices when there is no options on the dropdown

  // SAG
  protected readonly specialAccessGroups = signal(false);
  protected readonly allowSpecialAccessGroup = signal(false);
  protected readonly isGroupSAG = signal(false);
  protected readonly queueLength = signal(0);
  private sagModal: SpecialAccessGroupModalState = { mode: SpecialAccessGroupModalMode.REQUEST_ACCESS, open: false };
  public readonly crossRoomPresentationEnabled = signal(false);

  public constructor() {
    super();
    this.setDisplayName('PresentationMenuDropdownComponent');

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresenting) => {
        this.isPresenting.set(isPresenting);
      });

    this.store
      .select(socialAppSelectors.getPresenterDetails)
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.destroyed$))
      .subscribe((presenters: ExtendedPresenter[]) => {
        const mediaUser = presenters.find((slot: ExtendedPresenter) => slot.media);
        this.activeMediaEmbed.set(!!mediaUser?.media);
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enable_group_screen_share)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isEnabled) => {
        this.isGroupShareEnabled.set(isEnabled);
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.special_access_groups)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((specialAccessGroups) => {
        this.specialAccessGroups.set(specialAccessGroups);
      });
  }

  public ngOnInit(): void {
    // Initialize mobile fake dropdown state for menu types
    setTimeout(() => {
      this.updateDropdownState(PresentationMenuType.GROUP);
      this.updateDropdownState(PresentationMenuType.PRESENTATION);
    }, 0);

    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost) => {
        this.isHost.set(isHost);
      });

    this.store
      .select(socialAppSelectors.isPresentationActiveInAnotherRoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActiveInAnotherRoom) => {
        this.isSomeoneElsePresenting.set(isPresentationActiveInAnotherRoom);
      });

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => {
        this.isPresentationActive.set(isPresentationActive);
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.vimeo_embed)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((vimeo_embed) => {
        this.vimeoEmbed.set(vimeo_embed);
      });

    combineLatest([
      this.store.select(socialAppSelectors.isHost),
      this.sceneService.isFeatureAllowed(PHOTO_BOOTH),
      this.sceneService.isFeatureAllowed(ANYONE_CAN_PRESENT),
      this.sceneService.isFeatureAllowed(ANYONE_CAN_SHARE),
      this.sceneService.isFeatureAllowed(ANYONE_CAN_SHARE_TO_GROUP),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isHost, allowPhotoBooth, allowAnyonePresent, allowAnyoneShareScreen, allowGroupShareScreen]) => {
        this.isHost.set(isHost);
        this.allowPhotoBooth.set(!this.isMobile() && allowPhotoBooth);
        this.allowAnyonePresent.set(allowAnyonePresent);
        this.allowAnyoneShareScreen.set(allowAnyoneShareScreen);
        this.allowGroupShareScreen.set(!this.isMobile() && allowGroupShareScreen);
      });

    this.store
      .select(socialAppSelectors.getRoomSettings)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        const allowSpecialAccessGroup = this.sceneService.getSetting(SAG_ENABLED) ?? false;
        this.allowSpecialAccessGroup.set(!!allowSpecialAccessGroup);
      });

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(startWith({} as Social), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([room]) => {
        this.social.set(room);
      });

    this.store
      .select(socialAppSelectors.specialAccessGroupModal)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((sagModal) => {
        this.sagModal = sagModal;
        this.changeDetectorRef.markForCheck();
      });

    combineLatest([
      this.sceneService.primaryGroupMembers$.pipe(
        throttle(() => interval(PLAYERS_IN_VIEW_INTERVAL), { leading: true }),
      ),
      this.transitionService.isTransitioning$,
      this.transitionPresentationService.isTransitioning$,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([group, isTransitioning, isPresentationTransitioning]) => {
        this.group.set(group);
        const mainPlayer = this.sceneService.getPlayer();
        this.isPlayerOutsideGroupLimit.set(!!mainPlayer?.groupWarning);

        if (!this.groupShareScreenParticipantService.isGroupShareActiveSignal() || this.isPlayerOutsideGroupLimit()) {
          this.hasClosedMenu.set(false);
        }

        const shouldFlyoutBeOpen = !!(
          this.groupShareScreenParticipantService.isGroupShareActiveSignal() &&
          this.type() === PresentationMenuType.GROUP &&
          mainPlayer &&
          this.group().includes(mainPlayer) &&
          !this.isPlayerOutsideGroupLimit() &&
          !isTransitioning &&
          !isPresentationTransitioning &&
          !this.hasClosedMenu()
        );
        this.setDropdownVisibility(shouldFlyoutBeOpen);
        // on share end reset share consent
        if (!this.groupShareScreenParticipantService.isGroupShareActiveSignal()) {
          this.groupShareScreenParticipantService.setUserHasJoinedShare(false);
          this.changeDetectorRef.markForCheck();
          return;
        }
        this.changeDetectorRef.markForCheck();
      });

    combineLatest([
      this.store.select(socialAppSelectors.isConnectedRoom),
      this.store.select(socialAppSelectors.getCurrentConnectedRoomSet),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isConnectedRoom, currentConnectedRoomCounts]) => {
        this.isConnectedRoom.set(
          isConnectedRoom && !!currentConnectedRoomCounts?.socials && currentConnectedRoomCounts?.socials?.length > 1,
        );
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enhanced_screen_share)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enhanced_screen_share) => {
        this.enhancedScreenShare.set(enhanced_screen_share);
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_cross_room_presentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((crossRoomPresentationEnabled) => this.crossRoomPresentationEnabled.set(crossRoomPresentationEnabled));

    this.sceneService.sceneManager?.sagQueue$?.pipe(takeUntil(this.destroyed$)).subscribe((sagMap) => {
      const groupId = this.sceneService.sceneManager?.player?.groupId;
      if (!groupId) {
        return;
      }
      const usersGroup = sagMap.get(groupId);
      this.queueLength.set(usersGroup?.length ?? 0);
      this.isGroupSAG.set(this.sceneService.sceneManager?.player?.inSag ?? false);
    });
  }

  // A computed property that determines if the user can screen share with group
  protected readonly userCanShareWithGroup = computed(() => {
    return (
      this.isGroupShareEnabled() &&
      (this.allowGroupShareScreen() || this.isHost()) &&
      this.group().length > 1 &&
      !this.isPlayerOutsideGroupLimit() &&
      !this.groupShareScreenParticipantService.isGroupShareFull() &&
      !this.groupShareScreenParticipantService.hasMembersInScreenShare()
    );
  });

  // For backward compatibility during migration
  // public updateUserCanGroupScreenShare(): void {
  //   window.setTimeout(() => {
  //     this.userCanGroupScreenShare.set(this.userCanShareWithGroup());
  //     this.changeDetectorRef.markForCheck();
  //   }, 0);
  // }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    window.removeEventListener('click', this.windowClickListener);
    window.removeEventListener('touchstart', this.windowClickListener);
  }

  // Update the mobile fake dropdown state separately from showDropDownGroup
  private updateMobileFakeDropdown(type: PresentationMenuType): void {
    // Only perform this operation outside of template rendering (via timeout)
    if (type === PresentationMenuType.GROUP) {
      const allowSpecialAccessGroupValue = this.allowSpecialAccessGroup();

      if (!this.isMobile()) {
        this.isMobileFakeDropdown.set(false);
      } else {
        //For now, the fake button will be placed only if there is no option to show, in this case allowSpecialAccessGroup is the only possible.
        this.isMobileFakeDropdown.set(!allowSpecialAccessGroupValue);
      }
    }
  }

  // Computed properties for menu visibility by type
  // These are meant to be used in the template directly
  public readonly showGroupMenu = computed(() => {
    if (!this.isMobile()) {
      return (
        this.allowPhotoBooth() ||
        this.userCanShareWithGroup() ||
        this.isGroupSharing() ||
        this.allowSpecialAccessGroup()
      );
    } else {
      return true;
    }
  });

  public readonly showPresentationMenu = computed(() => {
    return this.allowAnyonePresent() || this.allowAnyoneShareScreen();
  });

  // For backward compatibility - this is the version that should be called directly from the template
  public showDropDownGroup(type: PresentationMenuType): boolean {
    // When called during template rendering, don't modify any signals
    switch (type) {
      case PresentationMenuType.GROUP:
        // Update mobile fake dropdown state later via timeout to avoid writing signals during render
        if (this.isMobile()) {
          window.setTimeout(() => this.updateMobileFakeDropdown(type), 0);
          return true;
        }
        return this.showGroupMenu();
      case PresentationMenuType.PRESENTATION:
        return this.showPresentationMenu();
      default:
        return false;
    }
  }

  // Called outside of template binding, like in lifecycle hooks or event handlers
  public updateDropdownState(type: PresentationMenuType): void {
    // It's safe to update signals here since this is called outside of rendering
    this.updateMobileFakeDropdown(type);
  }

  public readonly showDropdown = computed(() => this._showDropdown());

  // Update dropdown visibility and handle side effects
  public setDropdownVisibility(show: boolean): void {
    if (show === this._showDropdown()) {
      return;
    }
    this._showDropdown.set(show);
    this.showDropdownEvent.emit(show);
    // Set window listeners to catch clicks outside of the dropdown
    if (show) {
      this.addWindowListeners();
    } else {
      this.removeWindowListeners();
    }
  }

  public toggleDropdown($event: Event): void {
    if (!this.isMobileFakeDropdown()) {
      $event.stopPropagation();
      this.setDropdownVisibility(!this._showDropdown());
      // TODO: The 'emit' function requires a mandatory void argument
      this.dropdownSelected.emit();
    }
  }

  public dropdownClicked(e: Event): void {
    // Stop propagation so the dropdown doesn't close when clicked
    e.stopPropagation();
  }

  /**
   * Handle clicks on the window, to close the dropdown when the user clicks anywhere else on the window outside
   * of the dropdown
   **/
  private windowClickListener = (e: MouseEvent | TouchEvent) => {
    const { offsetParent } = e.target as HTMLElement;
    // Don't hide the dropdown if the toggle is being clicked
    if (
      offsetParent?.classList.contains(this.type() as string) &&
      offsetParent?.classList.contains('dropdown-button')
    ) {
      return;
    }
    this.setDropdownVisibility(false);
    this.closedFlyoutInGroupShare();
  };

  private addWindowListeners(): void {
    window.addEventListener('click', this.windowClickListener);
    window.addEventListener('touchstart', this.windowClickListener);
  }

  private removeWindowListeners(): void {
    window.removeEventListener('click', this.windowClickListener);
    window.removeEventListener('touchstart', this.windowClickListener);
  }

  // Handle click on start presentation
  public async handleQuickPresent(crossroom = false, mediaEmbedType?: MediaEmbedType | undefined): Promise<void> {
    this.logUserAction('quick present', 'clicked');

    if (!this.user()) {
      return;
    }

    const userCount = this.sceneService.getUserCount();
    const presentationSettings =
      userCount > 25 ? InteractionModePresets.ALL_HANDS : InteractionModePresets.OPEN_AUDIENCE_PARTICIPATION;

    await this.presentationService.startPresentation({
      selectedPresenters: [this.user() as User],
      settings: presentationSettings,
      isCrossRoom: crossroom,
      mediaEmbedType,
    });
  }

  public handleEmbedPresentation(crossroom = false) {
    const dialogRef = this.dialog.open(PresentationEmbedModalComponent);

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: { mediaEmbedType: MediaEmbedType; embedCode: string } | null) => {
        if (!result) {
          return;
        }

        if (!this.user()) {
          return;
        }

        const userCount = this.sceneService.getUserCount();

        const presentationSettings =
          userCount > 25 ? InteractionModePresets.ALL_HANDS : InteractionModePresets.OPEN_AUDIENCE_PARTICIPATION;

        if (this.isPresentationActive()) {
          void this.presentationService.updatePresentationMediaState({
            url: result.embedCode,
            playing: false,
            startTime: 0,
            playtingTime: 0,
          });
          return;
        }

        void this.presentationService.startPresentation({
          selectedPresenters: [this.user() as User],
          settings: presentationSettings,
          isCrossRoom: crossroom,
          mediaEmbedType: result.mediaEmbedType,
          embedCode: result.embedCode,
        });
      });
  }

  // Handle click on start screen share to room
  public async handleQuickScreenShare(enhanced = false): Promise<void> {
    this.logUserAction('quick screen share', 'clicked');

    if (!this.user()) {
      return;
    }
    const shareScreenSuccess = await this.shareScreenService.startSharing(enhanced);

    if (shareScreenSuccess) {
      if (!this.isPresentationActive()) {
        await this.presentationService.startPresentation({
          selectedPresenters: [this.user() as User],
        });
      }
    }
  }

  // Handle click on start screen share to group
  public async handleQuickGroupScreenShare(enhanced = false): Promise<void> {
    if (!this.user() || !this.user()?.userId) {
      this.error('handleQuickGroupScreenShare', 'No user found');
      return;
    }

    // if the user is already sharing to the group, stop sharing
    if (this.isGroupSharing() && this.groupShareScreenService.isHost()) {
      await this.groupShareScreenService.stopSharing(this.user()?.userId as string);
      return;
    }

    // if the user is a participant in the group share, stop sharing for them
    if (this.isGroupSharing() && this.groupShareScreenParticipantService.userHasJoinedShare()) {
      // Starts loading effect
      this.transitionService
        .enableFadeInBackground()
        .then(() => {
          this.groupShareScreenParticipantService.setUserHasJoinedShare(false);
        })
        .catch((err) => {
          this.error('Failed to execute transition', err);
        });

      return;
    }

    // if the user is a group member and the group share is active, join the group share
    if (this.isGroupSharing() && !this.groupShareScreenParticipantService.userHasJoinedShare()) {
      this.transitionService
        .enableDarkModeEffect(LoadingTransitionLabel.GROUPSHARE, true, true)
        .then(() => {
          this.groupShareScreenParticipantService.setUserHasJoinedShare(true);
        })
        .catch((err) => {
          this.error('Failed to execute transition', err);
        });

      return;
    }

    this.logUserAction('quick group screen share', 'clicked');

    await this.groupShareScreenService.startSharing(enhanced);
  }

  // Handle click on take a group photo
  public showPhotoBoothModal($event: Event): void {
    $event.stopPropagation();
    $event.preventDefault();
    const dialog = this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      panelClass: 'social-modal',
      data: {
        modalTitle: 'Photo booth is ready to start',
        text: 'The photo booth will take 3 consecutive photos of your room, which you can save and share with your friends and colleagues!',
        primaryButtonText: 'Snap Photos',
        primaryButtonIcon: 'camera_alt',
      },
    });
    dialog
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (result === 'primary') {
          this.photoBoothService.reset();
          this.photoBoothApproved();
        }
      });
  }

  private photoBoothApproved(): void {
    this.photoBoothService.boothStartedByUser.set(true);
    this.photoBoothService.boothInSession.set(false);
    this.startPhotoTakingProcess([
      { message: '{{name}} turned on the photo booth! Smile for the first picture in', duration: 5 },
      { message: 'Great! Snapping the next picture in', duration: 3 },
      { message: 'Last one! Smile in', duration: 3 },
    ]).catch((error) => {
      this.error('Failed to start photo booth', error);
    });
  }

  private async startPhotoTakingProcess(notifications: PhotoBoothNotification[]): Promise<void> {
    for (const notification of notifications) {
      this.sendMessageToPlayersInView(notification);
      const canceled = await this.photoBoothService.startCountdown(
        notification.message.replace('{{name}}', 'You'),
        notification.duration,
      );
      if (canceled) {
        return;
      }
      await this.photoBoothService.takePhoto(this.social()?.theme?.colorGradientBackground);
    }
    this.isLoading.set(true);
    this.loadingText.set(
      'We’re loading the preview of your pictures. This may take a minute. Don’t refresh or click the back button.',
    );
    const gif = await this.photoBoothService.createGif(this.photoBoothService.photos());
    this.photoBoothService.photos.update((photos) => [...photos, gif]);
    const viewerData = await this.generateViewerData();
    this.isLoading.set(false);
    this.openViewer(viewerData);
  }

  private sendMessageToPlayersInView(notification: PhotoBoothNotification): void {
    assertDefined(this.user());
    const message: Message = {
      type: MessageType.PHOTO_BOOTH_START,
      message: notification.message.replace('{{name}}', `${formatUserName(this.user() as User)}`),
      photoBoothData: { notificationDuration: notification.duration },
      onlyPlayers: this.sceneService.getPlayersIds(),
    };
    this.messageService.sendMessage(message);
  }

  private async generateViewerData(): Promise<PhotoViewerData> {
    const photoBoothPictures: PhotoBoothPicture[] = [];
    this.photoBoothService.photos().forEach((url, id) => photoBoothPictures.push({ url, id }));
    const framedPictures = await this.photoBoothService.addPreciateFrame(
      photoBoothPictures,
      moment().format('MM.DD.YYYY'),
      this.social()?.name ?? '',
    );
    return { photoBoothPictures, framedPictures };
  }

  private openViewer(data: PhotoViewerData): void {
    this.photoBoothService.boothStartedByUser.set(false);
    const dialog = this.dialog.open(PhotoBoothViewerComponent, {
      panelClass: 'photo-booth-viewer',
      data,
    });
    dialog.componentInstance.socialName.set((this.social()?.name ?? '').replace(/[^\w\s]/gi, ''));
    dialog
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (idSelectedToShare) => {
        if (!isNaN(idSelectedToShare)) {
          const selectedFramedPicture = data.framedPictures.find((fp) => fp.id === idSelectedToShare);
          if (!selectedFramedPicture) {
            return;
          }
          const url = await this.uploadPicture(selectedFramedPicture);
          this.dialog.open(SocialMediaShareModalComponent, {
            panelClass: 'photo-booth-viewer',
            data: {
              url,
              description: `This pic was snapped on #scootsocial - Create your own room at https://scoot.app`,
            },
          });
        }
      });
  }

  public async uploadPicture(imageToShare: FramedPicture): Promise<string> {
    this.isLoading.set(true);
    this.loadingText.set('Preparing your picture...');
    const { base64, height, width } = imageToShare;
    const type = imageToShare.type.split('/')[1];
    try {
      const uploadedImage = await this.socialMediaShareService.uploadImage(type, width, height, base64);
      const imageURI = `${uploadedImage.imageId}.${type}`;
      const baseUrl = 'https://scoot.app';
      return `${baseUrl}/imgs/public/${imageURI}`;
    } catch (error) {
      this.error('Failed to upload picture', error);
      throw error;
    } finally {
      this.isLoading.set(false);
    }
  }

  private closedFlyoutInGroupShare() {
    if (this.type() === 'menuGroup' && this.groupShareScreenParticipantService.userHasJoinedShare()) {
      this.hasClosedMenu.set(true);
    }
  }

  // SAG
  protected enableSpecialAccessGroup() {
    const msg: ConvertGroupMessage = {
      t: OutgoingMessageType.CONVERT_GROUP,
      sag: true,
    };
    this.websocketService.sendMessage(msg);
    this.changeDetectorRef.markForCheck();
  }

  protected disableSpecialAccessGroup() {
    const msg: ConvertGroupMessage = {
      t: OutgoingMessageType.CONVERT_GROUP,
      sag: false,
    };

    this.websocketService.sendMessage(msg);
    this.changeDetectorRef.markForCheck();
  }

  protected async openRequestQueue() {
    this._showDropdown.set(!this._showDropdown());
    // TODO: The 'emit' function requires a mandatory void argument
    this.dropdownSelected.emit();
    // await dropdown close animation for UX propouse
    await delayFor(200);
    this.store.dispatch(
      socialAppActions.UPDATE_SAG_MODAL({ mode: SpecialAccessGroupModalMode.ALLOW_ACCESS, open: true }),
    );
    this.changeDetectorRef.markForCheck();
  }

  protected readonly showNotification = computed(() => {
    return (
      !!this.queueLength() &&
      this.type() === 'menuGroup' &&
      this.specialAccessGroups() &&
      this.allowSpecialAccessGroup() &&
      this.isGroupSAG()
    );
  });

  protected readonly notificationText = computed(() => {
    return this.queueLength() > 9 ? '9+' : `${this.queueLength()}`;
  });

  // Computed property for group validity checking
  protected readonly hasValidGroup = computed(() => {
    return this.group().length > 1;
  });

  // Computed property for checking if user can initiate group actions
  protected readonly canInitiateGroupActions = computed(() => {
    return !this.isPlayerOutsideGroupLimit() && this.hasValidGroup();
  });

  // Computed property for SAG button visibility
  protected readonly showEnableSagButton = computed(() => {
    return this.allowSpecialAccessGroup() && this.specialAccessGroups() && !this.isGroupSAG() && !this.isSagModalOpen();
  });

  // Computed property for showing in-group button
  protected readonly showInGroupButton = computed(() => {
    return this.allowSpecialAccessGroup() && this.specialAccessGroups() && this.isGroupSAG();
  });

  // Computed property for SAG modal open state
  private readonly isSagModalOpen = computed(() => {
    return this.sagModal.open && this.sagModal.mode === SpecialAccessGroupModalMode.REQUEST_ACCESS;
  });
}

/**
 * IMPORTANT: SIGNAL USAGE GUIDELINES
 *
 * 1. NEVER modify signals during template rendering (in methods called from template bindings)
 * 2. Use computed() for read-only derived values that are used in templates
 * 3. For methods that need to update signals:
 *    - If called from template, don't modify signals directly - use setTimeout
 *    - If called from event handlers or lifecycle hooks, modifying signals is safe
 * 4. For complex state transitions, create dedicated methods that safely handle signal updates
 *
 * See the showDropDownGroup/updateDropdownState pattern for an example of how to safely
 * handle signals in methods that might be called during template rendering.
 */

/**
 * PresentationMenuDropdownComponent provides UI controls for various presentation options
 *
 * Signal conversion status:
 * - Initial properties like `isHost`, `isPresentationActive` have been converted to signals
 * - Added computed properties for values that derive from other signals
 * - Maintains some backward compatibility during transition period
 * - Group state is now reactive with signals
 * - Dropdown visibility uses computed properties and signals
 * - Added new computed properties for improved reactivity
 */
